import React from 'react';
import { Box, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import { fmtCount } from '../../utils/analyticsMetrics';
import { formatEt, formatEtShortRange } from '../../utils/formatEt';
import { analyticsUiTokens as T } from '../../constants/userAnalytics';

export const displayName = (item) => {
  const name = String(item?.name ?? '').trim();
  if (name) return name;
  const local = String(item?.email ?? '').split('@')[0].trim();
  return local || '—';
};

const initialsOf = (label) => {
  const words = label.split(/[\s._-]+/).filter(Boolean);
  if (!words.length || label === '—') return '?';
  return (words.length === 1 ? words[0].slice(0, 2) : `${words[0][0]}${words[words.length - 1][0]}`).toUpperCase();
};

const LOGIN_TYPES = {
  microsoft: { label: 'Microsoft', className: 'ua-badge-microsoft' },
  google: { label: 'Google', className: 'ua-badge-google' },
};

const LoginTypeBadge = ({ loginType }) => {
  const key = String(loginType ?? '').toLowerCase();
  if (!key) return <span className="ua-muted">—</span>;
  const known = LOGIN_TYPES[key];
  return (
    <span data-testid="login-type-badge" className={`ua-badge ${known?.className ?? ''}`}>
      {known?.label ?? `${key.charAt(0).toUpperCase()}${key.slice(1)}`}
    </span>
  );
};

// Every cell's look lives here, once, as nested selectors. Putting `sx` on each of the
// (up to 50 x 8) cells made the popup visibly slow to render; a class per role costs nothing.
const tableSx = {
  minWidth: 1000,
  '& .MuiTableCell-root': {
    fontSize: 12,
    color: T.slate[700],
    py: 1.5,
    borderBottom: '1px solid rgba(226,232,240,0.7)',
  },
  '& .MuiTableCell-head': {
    bgcolor: T.slate[100],
    color: T.slate[500],
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${T.cardBorder}`,
  },
  '& .ua-first': { pl: { xs: 2, sm: 3 } },
  '& .ua-last': { pr: { xs: 2, sm: 3 } },
  '& .ua-narrow': { width: 56 },
  '& .ua-num': { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  '& .ua-nowrap': { whiteSpace: 'nowrap' },
  '& .ua-index': { fontWeight: 600, color: T.slate[500] },
  '& .ua-email': { color: T.slate[600], fontWeight: 500 },
  '& .ua-muted': { color: T.slate[500] },
  '& .ua-user': { display: 'flex', alignItems: 'center', gap: '10px' },
  '& .ua-avatar': {
    width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    bgcolor: 'rgba(13,43,87,0.1)', color: T.navy, fontSize: 11, fontWeight: 700,
  },
  '& .ua-name': { fontSize: 12, fontWeight: 600, color: T.slate[800], whiteSpace: 'nowrap' },
  '& .ua-badge': { px: '8px', py: '2px', borderRadius: '4px', fontSize: 11, fontWeight: 600, bgcolor: T.slate[100], color: T.slate[700] },
  '& .ua-badge-microsoft': { bgcolor: T.sky[50], color: T.sky[800] },
  '& .ua-badge-google': { bgcolor: T.amber[50], color: T.amber[700] },
  '& .ua-inline': { display: 'inline-flex', alignItems: 'center', gap: '6px', color: T.slate[600] },
  '& .ua-inline .MuiSvgIcon-root': { fontSize: 14, color: T.slate[400] },
  '& .ua-pill': {
    display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 500, color: T.navy, bgcolor: T.slate[100],
    px: '10px', py: '4px', borderRadius: '6px',
  },
  '& .ua-pill .MuiSvgIcon-root': { fontSize: 14, color: T.emerald[600] },
  '& .ua-count-head': { display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', cursor: 'help' },
  '& .ua-count-period': { fontSize: 10, fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: T.slate[500] },
  '& tbody tr:hover': { bgcolor: 'rgba(248,250,252,0.9)' },
};

const CountHeader = ({ title, hint, period }) => (
  <Tooltip title={hint} arrow describeChild>
    <Box component="span" tabIndex={0} className="ua-count-head">
      <span><LocalText>{title}</LocalText></span>
      {period && <span className="ua-count-period">{period}</span>}
    </Box>
  </Tooltip>
);

const SKELETON_ROWS = 5;

/**
 * Presentational table for the popup. `loading` = first open (skeleton rows); `dimmed` = a page /
 * search refetch is running, so the old rows stay but fade (the table is never blanked).
 */
const UsersTable = ({ items, offset, activity, loading, dimmed }) => {
  const cmsTooltip = useLocalText("Account created — CMS logins aren't recorded");
  const sessionsHint = useLocalText('One login/session record in this period');
  const daysHint = useLocalText('Days (ET) with at least one session');
  const period = activity ? formatEtShortRange(activity.from, activity.to) : '';

  return (
    <Table stickyHeader size="small" sx={tableSx} data-testid="users-table">
      <TableHead>
        <TableRow>
          <TableCell className="ua-first ua-narrow">#</TableCell>
          <TableCell><LocalText>User Name</LocalText></TableCell>
          <TableCell><LocalText>User Email</LocalText></TableCell>
          <TableCell><LocalText>Login Type</LocalText></TableCell>
          <TableCell><LocalText>First Login</LocalText></TableCell>
          <TableCell><LocalText>Last Login</LocalText></TableCell>
          <TableCell className="ua-num"><CountHeader title="Total Sessions" hint={sessionsHint} period={period} /></TableCell>
          <TableCell className="ua-num ua-last"><CountHeader title="Total Active Days" hint={daysHint} period={period} /></TableCell>
        </TableRow>
      </TableHead>

      <TableBody sx={{ opacity: dimmed ? 0.5 : 1, transition: 'opacity 0.18s ease' }} aria-busy={dimmed || undefined}>
        {loading
          ? Array.from({ length: SKELETON_ROWS }, (_, i) => (
            <TableRow key={`sk-${i}`} data-testid="users-skeleton-row">
              {Array.from({ length: 8 }, (_c, c) => (
                <TableCell key={c}><Skeleton variant="text" width={c === 0 ? 20 : '70%'} /></TableCell>
              ))}
            </TableRow>
          ))
          : items.map((item, i) => {
            const name = displayName(item);
            const cmsOnly = item.product === 'cms';
            const firstLogin = (
              <>
                <EventAvailableOutlinedIcon />
                {formatEt(item.firstLoginAt)}
              </>
            );
            return (
              <TableRow key={item.userId ?? `${offset}-${i}`}>
                <TableCell className="ua-first ua-index">{offset + i + 1}</TableCell>
                <TableCell>
                  <span className="ua-user">
                    <span className="ua-avatar" aria-hidden="true">{initialsOf(name)}</span>
                    <span className="ua-name">{name}</span>
                  </span>
                </TableCell>
                <TableCell className="ua-email">{item.email || '—'}</TableCell>
                <TableCell><LoginTypeBadge loginType={item.loginType} /></TableCell>
                <TableCell className="ua-nowrap">
                  {cmsOnly ? (
                    <Tooltip title={cmsTooltip} arrow describeChild>
                      <span tabIndex={0} data-testid="first-login-cms-only" className="ua-inline" style={{ cursor: 'help' }}>{firstLogin}</span>
                    </Tooltip>
                  ) : (
                    <span className="ua-inline">{firstLogin}</span>
                  )}
                </TableCell>
                <TableCell className="ua-nowrap">
                  {item.lastLoginAt ? (
                    <span className="ua-pill">
                      <AccessTimeFilledIcon />
                      {formatEt(item.lastLoginAt)}
                    </span>
                  ) : '—'}
                </TableCell>
                <TableCell className="ua-num">{fmtCount(item.sessionCount)}</TableCell>
                <TableCell className="ua-num ua-last">{fmtCount(item.activeDays)}</TableCell>
              </TableRow>
            );
          })}
      </TableBody>
    </Table>
  );
};

export default UsersTable;
